import { useEffect, useRef, useState } from "react";
import {
  flowsAtContentPoint,
  type SourceRegion,
} from "@/components/pdf-viewer/regionGeometry";
import type { ImageBand } from "@/app/recap/types/delineation.types";
import { ImageHighlightLayers } from "./ImageHighlightLayers";

/** Scrollable + pannable image viewer with click-drag support. */
export function ImageViewer({
  src,
  alt,
  scale,
  rotation,
  imageBands = [],
  sourceRegions = [],
  restingBands = [],
  onSourceHover,
}: Readonly<{
  src: string;
  alt: string;
  scale: number;
  rotation: number;
  imageBands?: ImageBand[];
  /** Every flow's source region on this photo, for the reverse hover hit-test
   *  (STK-2338: doc → flow). */
  sourceRegions?: SourceRegion[];
  /** Neutral "what did the AI read" layer, shown while the pointer is inside
   *  the preview — separate from `imageBands` (the strong hover band) because
   *  band ids are reused across the single-flow / multi-flow walks that feed
   *  each list and must stay in their own sibling-keyed `.map()`. */
  restingBands?: ImageBand[];
  onSourceHover?: (flowIds: string[] | null) => void;
}>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [content, setContent] = useState<ImageContentRect | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const [naturalSize, setNaturalSize] = useState<{
    w: number;
    h: number;
  } | null>(null);
  const [containerSize, setContainerSize] = useState<{
    w: number;
    h: number;
  } | null>(null);

  // Reset cached dimensions when the image source changes. `content` must go
  // too: it is the box every band and the hover hit-area are positioned in, so
  // holding the PREVIOUS image's measurement paints the old bands over the new
  // photo until the ResizeObserver re-measures (cubic P2). Both overlays are
  // gated on `content`, so clearing it withholds them until the new image is
  // laid out — which is the honest state.
  useEffect(() => {
    setNaturalSize(null);
    setContent(null);
  }, [src]);

  // Measure the image's real content box (LAYOUT geometry, which the CSS
  // transform doesn't touch) so hover bands anchor to the image and stay
  // aligned at any zoom / rotation (STK-1299).
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const measure = () => {
      setContent(
        computeImageContentRect(
          { width: img.naturalWidth, height: img.naturalHeight },
          {
            width: img.offsetWidth,
            height: img.offsetHeight,
            offsetLeft: img.offsetLeft,
            offsetTop: img.offsetTop,
          },
        ),
      );
    };
    if (img.complete) measure();
    img.addEventListener("load", measure);
    const ro = new ResizeObserver(measure);
    ro.observe(img);
    return () => {
      img.removeEventListener("load", measure);
      ro.disconnect();
    };
  }, [src]);

  // Measure the scrollable container so we can compute fit-to-view dimensions.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (typeof ResizeObserver === "undefined") {
      // Fallback: one-shot measurement via getBoundingClientRect.
      const { width, height } = el.getBoundingClientRect();
      setContainerSize({ w: width, h: height });
      return;
    }
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // --- Pan via click+drag -------------------------------------------------
  // Pointer events, not mouse events: a drag-to-pan surface has to answer for
  // mouse, pen AND touch, and `pointerdown` is the one API that does (the
  // mouse-only pair also made this scroll container read as a fake interactive
  // widget to a11y linting, Sonar S6848). Touch is explicitly excluded — the
  // container already scrolls natively on touch, and panning it as well would
  // move the photo at double speed.
  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === "touch") return;
    if (e.button !== 0 || !containerRef.current) return;
    setIsPanning(true);
    panStartRef.current = {
      x: e.clientX + containerRef.current.scrollLeft,
      y: e.clientY + containerRef.current.scrollTop,
    };
    e.preventDefault();
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isPanning || !containerRef.current) return;
    containerRef.current.scrollLeft = panStartRef.current.x - e.clientX;
    containerRef.current.scrollTop = panStartRef.current.y - e.clientY;
  };

  const endPan = () => setIsPanning(false);

  // --- Reverse hover (doc → flow), STK-2338 -------------------------------
  // `offsetX/offsetY` are in the hover surface's OWN local box, which lives
  // inside the rotation wrapper — so the user's rotate() is already accounted
  // for by the DOM, and only the document's own tilt needs inverting
  // (flowsAtContentPoint's job). A getBoundingClientRect subtraction would
  // instead give the rotated element's axis-aligned bounding box, which is
  // wrong whenever `rotation !== 0`.
  const handleSourceHoverMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isPanning) return;
    // Not a silent return when there is nothing to hit-test: emitting null is
    // the truthful answer for "the pointer is over no region of this document",
    // and swallowing it would leave a previously-hovered flow ringed
    // (Constitution 2.1.8, cubic P2). The document-level reset lives in
    // `useSourceHoverWiring`; this is the per-move half of the same rule.
    if (!content || !sourceRegions.length) {
      onSourceHover?.(null);
      return;
    }
    onSourceHover?.(
      flowsAtContentPoint(
        sourceRegions,
        { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY },
        content,
      ),
    );
  };

  // --- Compute displayed image dimensions --------------------------------
  const PADDING = 32; // 16 px each side
  let imgW: number | undefined;
  let imgH: number | undefined;

  if (naturalSize && containerSize) {
    const availW = containerSize.w - PADDING;
    const availH = containerSize.h - PADDING;
    // Base size: fit-to-contain at scale=1 (never upscale beyond natural).
    const fitRatio = Math.min(
      availW / naturalSize.w,
      availH / naturalSize.h,
      1,
    );
    imgW = naturalSize.w * fitRatio * scale;
    imgH = naturalSize.h * fitRatio * scale;
  }

  // 90°/270° rotations swap the visual bounding-box dimensions. `imgW`/`imgH`
  // are set together or not at all, so the swapped pair is too.
  const isOrthogonal = rotation % 180 !== 0;
  const [visualW, visualH] = isOrthogonal ? [imgH, imgW] : [imgW, imgH];
  const wrapperW = visualW == null ? undefined : visualW + PADDING;
  const wrapperH = visualH == null ? undefined : visualH + PADDING;

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-auto bg-muted"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endPan}
      onPointerCancel={endPan}
      onPointerLeave={endPan}
      style={{ cursor: isPanning ? "grabbing" : "grab" }}
    >
      <div
        className="flex items-center justify-center"
        style={{
          minWidth: "100%",
          minHeight: "100%",
          width: wrapperW,
          height: wrapperH,
        }}
      >
        {/* Bands share this rotation wrapper with the <img> so both the
            fit-to-view sizing (real layout px) and rotation move image +
            bands together; the bands are anchored to the image's measured
            content box, staying aligned through zoom and rotation
            (STK-1299 bands over STK-1352 scroll/pan). */}
        <div
          className="relative"
          style={{
            transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
          }}
        >
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            onLoad={(e) =>
              setNaturalSize({
                w: e.currentTarget.naturalWidth,
                h: e.currentTarget.naturalHeight,
              })
            }
            draggable={false}
            className="select-none block"
            style={{
              width: imgW,
              height: imgH,
              ...(imgW == null && {
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain" as const,
              }),
            }}
          />
          {content && (
            <ImageHighlightLayers
              content={content}
              imageBands={imageBands}
              restingBands={restingBands}
            />
          )}
          {/* Reverse-hover surface, rendered last. Every band above keeps
              pointerEvents:"none", so this is the only band-layer element
              that ever receives pointer events. No stopPropagation: the
              container's pan handler relies on this mousedown bubbling up
              (AC 10). */}
          {content && onSourceHover && (
            <div
              data-testid="photo-hover-surface"
              style={{
                position: "absolute",
                left: content.left,
                top: content.top,
                width: content.width,
                height: content.height,
              }}
              onMouseMove={handleSourceHoverMove}
              onMouseLeave={() => onSourceHover(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** The image's actual rendered content box, in the wrapper's LAYOUT (pre-
 *  transform) pixel space — accounts for object-contain letterboxing. */
export interface ImageContentRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Reproduce `object-contain`: scale the natural image to fit inside the
 * element box (preserving aspect), centered, and return that content box in
 * the element's offset-parent coordinates. Returns null when any dimension is
 * 0 (image not laid out / not loaded yet). Pure — unit-tested without a DOM.
 */
export function computeImageContentRect(
  natural: { width: number; height: number },
  element: {
    width: number;
    height: number;
    offsetLeft: number;
    offsetTop: number;
  },
): ImageContentRect | null {
  const { width: nw, height: nh } = natural;
  const { width: ew, height: eh, offsetLeft, offsetTop } = element;
  if (!nw || !nh || !ew || !eh) return null;
  const fit = Math.min(ew / nw, eh / nh);
  const cw = nw * fit;
  const ch = nh * fit;
  return {
    left: offsetLeft + (ew - cw) / 2,
    top: offsetTop + (eh - ch) / 2,
    width: cw,
    height: ch,
  };
}
